import React, { useEffect, useState } from "react";
import { Cpu, Search, RotateCcw, ChevronUp, ChevronDown } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown ";

interface HardwareData {
    id: number;
    component_type: string;
    name: string;
    description: string;
    serial_number: string;
    model_name: string;
    status: string | null;
}

const SwitchHardwareInfo: React.FC = () => {
    const [data, setData] = useState<HardwareData[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination state
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;

    const fetchData = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * itemsPerPage;
            const limit = itemsPerPage;

            const queryObj: any = { asset_id: asset_id };
            if (debouncedSearchTerm) {
                queryObj["$or"] = [
                    { name: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { component_type: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { model_name: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { serial_number: { "$ilike": `%${debouncedSearchTerm}%` } }
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip,
                limit,
            };

            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/hardware_info", { params });
            if (response.data.status) {
                setData(response.data.data || []);
                setTotalItems(response.data.total || 0);
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        fetchData();
    }, [currentPage, itemsPerPage, sortConfig, debouncedSearchTerm]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);



    return (
        <div className="space-y-4 animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <Cpu className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-sm font-bold text-white leading-tight">Hardware Information</h2>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search hardware..."
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-orange-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={() => fetchData()}
                        className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white"
                    >
                        <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-900/50 border-b border-gray-700">
                                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Component Type</th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap" onClick={() => handleSort('name')}>
                                    <div className="flex items-center gap-1 cursor-pointer">Name {sortConfig?.key === 'name' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</div>
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Description</th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Serial Number</th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Model Name</th>
                                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="py-20 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <RotateCcw className="w-8 h-8 text-orange-500 animate-spin" />
                                            <span className="text-gray-400">Loading hardware info...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-20 text-center text-gray-500">
                                        No hardware information found
                                    </td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-700/30 transition-colors">
                                        <td className="px-4 py-3 text-sm font-medium text-white whitespace-nowrap">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-500/10 text-blue-500 border border-blue-500/20">
                                                {item.component_type}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">{item.name || '-'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-400 whitespace-nowrap">{item.description || '-'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-300 font-mono whitespace-nowrap">{item.serial_number && item.serial_number !== '""' ? item.serial_number : '-'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">{item.model_name && item.model_name !== '""' ? item.model_name : '-'}</td>
                                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                                            {item.status ? (
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase ${item.status === 'OK' || item.status === 'up'
                                                    ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                                                    : 'bg-red-500/10 text-red-500 border border-red-500/20'
                                                    }`}>
                                                    {item.status}
                                                </span>
                                            ) : '-'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="bg-gray-900/40 px-4 py-2 border-t border-gray-700">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">Show</span>
                                <ItemsPerPageDropdown
                                    value={itemsPerPage}
                                    onChange={(val) => {
                                        setItemsPerPage(val);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                            <span className="text-xs text-gray-400">
                                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                className="p-2 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                            >
                                &laquo;
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="p-2 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                            >
                                &lsaquo;
                            </button>
                            <div className="bg-orange-600 text-white px-3 py-1 rounded text-sm font-bold">
                                {currentPage}
                            </div>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-2 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                            >
                                &rsaquo;
                            </button>
                            <button
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-2 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                            >
                                &raquo;
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SwitchHardwareInfo;
